import { useRef, useState } from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Card,
    CardMedia,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadIcon from "@mui/icons-material/UploadFile";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { usePropertyId } from "../../../../../common/hooks/usePropertyId";
import { useWebsiteContentData } from "../../../../../common/hooks/useWebsiteContentData";
import { withQueryData } from "../../../../../common/withQueryData";
import { useAlertNotification } from "../../../../../common/hooks/useAlertNotification";

/** Extracts the filename stem (no extension) from a full URL. */
const stemFromUrl = (url) =>
    url.split("/").pop()?.replace(/\.[^.]+$/, "") ?? url;

// ── Images Manager ────────────────────────────────────────────────────────────

const ImagesManager = ({ component, uploadImages, deleteImages, onError }) => {
    const fileInputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [deletingStems, setDeletingStems] = useState(new Set());
    const [copiedStem, setCopiedStem] = useState(null);

    const images = component.images ?? [];

    const handleUpload = async (e) => {
        const files = Array.from(e.target.files);
        if (!files.length) return;
        e.target.value = "";
        setUploading(true);
        try {
            await uploadImages(component.id, files);
        } catch (error) {
            onError(error);
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (stem) => {
        setDeletingStems((prev) => new Set(prev).add(stem));
        try {
            await deleteImages(component.id, [stem]);
        } catch (error) {
            onError(error);
        } finally {
            setDeletingStems((prev) => {
                const next = new Set(prev);
                next.delete(stem);
                return next;
            });
        }
    };

    const handleCopy = (stem) => {
        navigator.clipboard.writeText(stem).then(() => {
            setCopiedStem(stem);
            setTimeout(() => setCopiedStem(null), 1500);
        });
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Typography variant="subtitle2" color="text.secondary">
                    Images
                </Typography>
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={uploading ? <CircularProgress size={14} /> : <UploadIcon />}
                    disabled={uploading}
                    onClick={() => fileInputRef.current?.click()}
                >
                    {uploading ? "Uploading…" : "Upload"}
                </Button>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    hidden
                    onChange={handleUpload}
                />
            </Box>

            {images.length === 0 ? (
                <Typography variant="body2" color="text.disabled" sx={{ fontStyle: "italic" }}>
                    No images uploaded yet.
                </Typography>
            ) : (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                    {images.map((url) => {
                        const stem = stemFromUrl(url);
                        const isDeleting = deletingStems.has(stem);
                        return (
                            <Card
                                key={url}
                                variant="outlined"
                                sx={{ width: 160, flexShrink: 0, position: "relative" }}
                            >
                                <CardMedia
                                    component="img"
                                    image={url}
                                    alt={stem}
                                    sx={{ height: 110, objectFit: "cover" }}
                                />
                                <Box
                                    sx={{
                                        px: 1,
                                        py: 0.75,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                        bgcolor: "grey.50",
                                    }}
                                >
                                    <Tooltip
                                        title={copiedStem === stem ? "Copied!" : "Copy filename stem"}
                                        placement="top"
                                    >
                                        <Chip
                                            label={stem}
                                            size="small"
                                            onClick={() => handleCopy(stem)}
                                            icon={<ContentCopyIcon sx={{ fontSize: "0.8rem !important" }} />}
                                            sx={{
                                                fontFamily: "monospace",
                                                fontSize: "0.7rem",
                                                maxWidth: 110,
                                                cursor: "pointer",
                                                bgcolor: copiedStem === stem ? "success.light" : undefined,
                                            }}
                                        />
                                    </Tooltip>
                                    <Tooltip title="Delete image">
                                        <IconButton
                                            size="small"
                                            color="error"
                                            disabled={isDeleting}
                                            onClick={() => handleDelete(stem)}
                                            sx={{ ml: "auto" }}
                                        >
                                            {isDeleting ? (
                                                <CircularProgress size={14} color="error" />
                                            ) : (
                                                <DeleteIcon fontSize="small" />
                                            )}
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </Card>
                        );
                    })}
                </Box>
            )}
        </Box>
    );
};

// ── Component Editor ──────────────────────────────────────────────────────────

const ComponentEditor = ({ component, onSave, uploadImages, deleteImages, onError }) => {
    const [jsonValue, setJsonValue] = useState(
        component.defaultProps != null
            ? JSON.stringify(component.defaultProps, null, 2)
            : ""
    );
    const [jsonError, setJsonError] = useState(null);
    const [saving, setSaving] = useState(false);

    const handleChange = (e) => {
        setJsonValue(e.target.value);
        setJsonError(null);
    };

    const handleSave = async () => {
        let parsedProps;
        if (jsonValue.trim() === "") {
            parsedProps = null;
        } else {
            try {
                parsedProps = JSON.parse(jsonValue);
            } catch {
                setJsonError("Invalid JSON — please fix the syntax before saving.");
                return;
            }
        }
        setSaving(true);
        try {
            await onSave(component.id, { ...component, defaultProps: parsedProps });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, width: "100%" }}>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Typography variant="body2" color="text.secondary">
                    <strong>ID:</strong> {component.id}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    <strong>Type:</strong> {component.type}
                </Typography>
            </Box>

            <Divider />

            <ImagesManager
                component={component}
                uploadImages={uploadImages}
                deleteImages={deleteImages}
                onError={onError}
            />

            <Divider />

            <TextField
                label="defaultProps (JSON)"
                multiline
                minRows={6}
                maxRows={20}
                fullWidth
                value={jsonValue}
                onChange={handleChange}
                error={!!jsonError}
                helperText={jsonError}
                inputProps={{ style: { fontFamily: "monospace", fontSize: "0.85rem" } }}
            />

            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    disabled={saving}
                >
                    {saving ? "Saving…" : "Save"}
                </Button>
            </Box>
        </Box>
    );
};

// ── Components List ───────────────────────────────────────────────────────────

const WebsiteComponentsList = ({ data: components, propertyId, onSaved, onError }) => {
    const { updateComponent, uploadImages, deleteImages } = useWebsiteContentData(propertyId);
    const [expanded, setExpanded] = useState(false);

    const handleAccordionChange = (componentId) => (_, isExpanded) => {
        setExpanded(isExpanded ? componentId : false);
    };

    const handleSave = async (componentId, updatedComponent) => {
        try {
            await updateComponent(componentId, updatedComponent);
            onSaved(componentId);
        } catch (error) {
            onError(error);
        }
    };

    if (!components?.length) {
        return (
            <Typography variant="body1" sx={{ mt: 2, textAlign: "center", color: "text.secondary" }}>
                No website components found for this property.
            </Typography>
        );
    }

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {components.map((component) => (
                <Accordion
                    key={component.id}
                    expanded={expanded === component.id}
                    onChange={handleAccordionChange(component.id)}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <Typography fontWeight={500}>{component.id}</Typography>
                            <Chip label={component.type} size="small" variant="outlined" />
                            {component.images?.length > 0 && (
                                <Chip
                                    label={`${component.images.length} image${component.images.length !== 1 ? "s" : ""}`}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                />
                            )}
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                        {expanded === component.id && (
                            <ComponentEditor
                                component={component}
                                onSave={handleSave}
                                uploadImages={uploadImages}
                                deleteImages={deleteImages}
                                onError={onError}
                            />
                        )}
                    </AccordionDetails>
                </Accordion>
            ))}
        </Box>
    );
};

const WebsiteComponentsListWithQueryData = withQueryData(WebsiteComponentsList);

// ── Page ──────────────────────────────────────────────────────────────────────

const WebsiteContentPage = () => {
    const { setAlert } = useAlertNotification();
    const propertyId = usePropertyId();
    const { queryResults } = useWebsiteContentData(propertyId);

    const onSaved = () =>
        setAlert({ message: "Component saved successfully.", severity: "success" });

    const onError = (error) =>
        setAlert({ message: error.message, severity: "error" });

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h5" gutterBottom>
                Website Content
            </Typography>
            <WebsiteComponentsListWithQueryData
                queryData={queryResults}
                propertyId={propertyId}
                onSaved={onSaved}
                onError={onError}
            />
        </Box>
    );
};

export default WebsiteContentPage;
